/**
 * useAIWeekGenerator - Hook d'intégration complète du module ai-planner
 *
 * Intègre TOUTES les fonctionnalités de gamification et génération IA:
 * - Génération de planning avec Groq (LLaMA)
 * - Système de compagnons (Luna, Spark, Zen)
 * - Quêtes et défis adaptatifs
 * - Suggestions d'activités locales
 * - Recommandations intelligentes
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { saveUserProfile as saveProfileFirestore, getUserProfile as getProfileFirestore, isAuthenticated } from '../services/firestoreSync';

const PROFILE_STORAGE_KEY = 'adhd-user-profile';
const COMPANION_STORAGE_KEY = 'adhd-companion';
const QUESTS_STORAGE_KEY = 'adhd-active-quests';

// Groq API configuration (free, fast inference)
const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_MODELS = [
  'llama-3.3-70b-versatile',           // Best for complex JSON generation
  'llama-3.1-8b-instant',              // Fast fallback
  'mixtral-8x7b-32768',                // Alternative
];

// ============ COMPAGNONS ============
const COMPANIONS = {
  luna: {
    id: 'luna',
    name: 'Luna',
    emoji: '🦊',
    personality: 'douce et encourageante',
    color: '#f472b6',
    energyStyle: 'calm',
    greetings: [
      'Coucou ! Comment tu te sens aujourd\'hui ?',
      'Je suis là pour toi ! On avance ensemble ?',
      'Chaque petit pas compte, tu le sais ?'
    ],
    encouragements: [
      'Tu peux le faire, je crois en toi !',
      'Chaque petit pas compte !',
      'On avance ensemble !',
      'Tu es plus fort(e) que tu ne le penses !',
      'Bravo pour ton effort ! 🌸'
    ],
    completions: [
      'Génial ! Tu as réussi ! 🎉',
      'Je savais que tu pouvais le faire !',
      'Encore une victoire ! Continue comme ça !'
    ]
  },
  spark: {
    id: 'spark',
    name: 'Spark',
    emoji: '⚡',
    personality: 'énergique et motivant',
    color: '#fbbf24',
    energyStyle: 'high',
    greetings: [
      'HEY ! Prêt(e) à tout déchirer ?!',
      'Mode BOSS activé ! On y va !',
      'C\'est NOTRE jour ! Let\'s GO !'
    ],
    encouragements: [
      'ALLONS-Y ! Tu vas tout déchirer !',
      'Mode BOSS activé !',
      'Rien ne peut t\'arrêter !',
      'C\'est NOTRE moment !',
      'Tu es en FEU ! 🔥'
    ],
    completions: [
      'BOOM ! Tâche TERMINÉE ! 💥',
      'Tu es INCROYABLE !',
      'Victoire ÉPIQUE !'
    ]
  },
  zen: {
    id: 'zen',
    name: 'Zen',
    emoji: '🐢',
    personality: 'sage et patient',
    color: '#34d399',
    energyStyle: 'low',
    greetings: [
      'Respire... On prend notre temps.',
      'Pas de pression. Une chose à la fois.',
      'Aujourd\'hui, on fait de son mieux.'
    ],
    encouragements: [
      'Respire... une chose à la fois.',
      'La patience est une force.',
      'Même les petites victoires comptent.',
      'Tu fais exactement ce qu\'il faut.',
      'Doucement mais sûrement 🌿'
    ],
    completions: [
      'Bien joué. Tu as pris ton temps, et c\'est parfait.',
      'Voilà. Une chose de faite.',
      'Tu vois ? Pas besoin de courir.'
    ]
  }
};

// ============ TYPES DE QUÊTES ============
const QUEST_TYPES = {
  daily: { name: 'Quête du jour', icon: '☀️', xpMultiplier: 1.0 },
  streak: { name: 'Défi Flamme', icon: '🔥', xpMultiplier: 1.5 },
  boss: { name: 'Boss de la semaine', icon: '👑', xpMultiplier: 2.0 },
  secret: { name: 'Quête secrète', icon: '🎁', xpMultiplier: 3.0 },
  recovery: { name: 'Mode doux', icon: '🌸', xpMultiplier: 1.2 }
};

// ============ CATÉGORIES D'ACTIVITÉS ============
const ACTIVITY_CATEGORIES = {
  vital: { id: 'vital', name: 'Vital', icon: '💊', color: '#ef4444' },
  sport: { id: 'sport', name: 'Sport', icon: '🏃', color: '#22c55e' },
  courses: { id: 'courses', name: 'Courses', icon: '🛒', color: '#f59e0b' },
  travail: { id: 'travail', name: 'Travail', icon: '💼', color: '#3b82f6' },
  loisirs: { id: 'loisirs', name: 'Loisirs', icon: '🎮', color: '#8b5cf6' },
  social: { id: 'social', name: 'Social', icon: '👥', color: '#ec4899' },
  menage: { id: 'menage', name: 'Ménage', icon: '🧹', color: '#64748b' },
  repos: { id: 'repos', name: 'Repos', icon: '😴', color: '#6366f1' },
  hygiene: { id: 'hygiene', name: 'Hygiène', icon: '🚿', color: '#0ea5e9' },
  repas: { id: 'repas', name: 'Repas', icon: '🍽️', color: '#f97316' }
};

// ============ MAPPING CATÉGORIES ============
const CATEGORY_MAP = {
  vital: 'vital',
  sport: 'health',
  courses: 'personal',
  travail: 'work',
  loisirs: 'personal',
  social: 'social',
  menage: 'personal',
  repos: 'personal',
  hygiene: 'vital',
  repas: 'vital',
  sante: 'health'
};

/**
 * Profil par défaut - Structure complète pour génération IA
 */
const getDefaultProfile = () => ({
  // Informations de base
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  age: null,
  sex: null,
  city: '',
  country: 'France',

  // Lieux favoris et hobbies
  nearbyPlaces: [],
  hobbies: [],

  // Emploi du temps professionnel
  workSchedule: {
    type: 'flexible', // 'fixed', 'flexible', 'unemployed', 'student', 'freelance'
    startTime: '09:00',
    endTime: '18:00',
    workDays: [1, 2, 3, 4, 5], // 0=Dim, 1=Lun, ...
    workFromHome: false,
    jobTitle: '',
    constraints: '' // Ex: "réunions le mardi matin"
  },

  // Santé et médicaments
  healthInfo: {
    hasMedication: false,
    medicationTimes: [], // ["08:00", "20:00"]
    medicationName: '', // "Ritaline, Concerta"
    hasTherapy: false,
    therapyDay: '', // "Mercredi"
    therapyTime: '',
    healthConstraints: '' // "Fatigue chronique", "Anxiété"
  },

  // Préférences quotidiennes
  preferences: {
    wakeTime: '07:00',
    sleepTime: '23:00',
    mealTimes: {
      breakfast: '07:30',
      lunch: '12:30',
      dinner: '19:30'
    },
    exerciseFrequency: 'moderate', // 'none', 'light', 'moderate', 'intense'
    exercisePreference: [], // ["course", "yoga", "musculation"]
    socialLevel: 'moderate', // 'low', 'moderate', 'high'
    focusPeakTime: 'morning', // 'morning', 'afternoon', 'evening'
    needsBreaks: true,
    breakFrequency: 45, // minutes entre pauses
  },

  // Contraintes et difficultés ADHD
  adhdInfo: {
    diagnosisType: '', // 'TDAH', 'TDA', 'non-diagnostiqué'
    mainChallenges: [], // ['concentration', 'organisation', 'procrastination']
    copingStrategies: [], // ['timer', 'musique', 'récompenses']
  },

  // Transport
  transport: {
    hasVehicle: false,
    preferredTransport: 'walking', // 'walking', 'bike', 'car', 'public'
    maxTravelTime: 30, // minutes
  },

  // Objectifs actuels
  goals: {
    weeklyGoals: [], // ["Faire du sport 3x", "Ranger ma chambre"]
    currentPriority: '', // "Chercher un emploi", "Examens", "Santé"
  }
});

/**
 * Hook principal - Intégration complète ai-planner
 */
export function useAIWeekGenerator() {
  // États principaux
  const [profile, setProfileState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastGeneration, setLastGeneration] = useState(null);

  // États compagnon
  const [companion, setCompanion] = useState(null);
  const [companionMessage, setCompanionMessage] = useState('');

  // États quêtes
  const [activeQuests, setActiveQuests] = useState([]);
  const [questProgress, setQuestProgress] = useState({});

  // États suggestions
  const [suggestions, setSuggestions] = useState([]);

  // Charger les données au montage
  useEffect(() => {
    loadProfile();
    loadCompanion();
    loadQuests();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recharger le profil quand Firebase Auth change (token disponible = Firestore accessible)
  useEffect(() => {
    const handleAuthChange = () => {
      // Delayed reload to let Firestore initialize
      setTimeout(() => loadProfile(), 1500);
    };
    window.addEventListener('firebase-auth-ready', handleAuthChange);
    // Also retry after 3s in case auth was already ready
    const retryTimer = setTimeout(() => {
      if (!profile?.firstName) loadProfile();
    }, 3000);
    return () => {
      window.removeEventListener('firebase-auth-ready', handleAuthChange);
      clearTimeout(retryTimer);
    };
  }, [profile?.firstName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============ PROFIL ============
  /**
   * Enrichit un profil avec les données Google Auth si des champs sont vides.
   */
  const enrichWithGoogleAuth = useCallback((prof) => {
    const enriched = { ...prof };
    try {
      const authRaw = localStorage.getItem('adhd-planner-google-auth');
      if (authRaw) {
        const { user } = JSON.parse(authRaw);
        if (user) {
          if (!enriched.firstName && user.name) {
            enriched.firstName = user.name.split(' ')[0];
          }
          if (!enriched.lastName && user.name) {
            const parts = user.name.split(' ');
            if (parts.length > 1) enriched.lastName = parts.slice(1).join(' ');
          }
          if (!enriched.email && user.email) {
            enriched.email = user.email;
          }
        }
      }
    } catch { /* ignore */ }
    return enriched;
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      // 1. Essayer Firestore d'abord (source de vérité)
      let firestoreProfile = null;
      if (isAuthenticated()) {
        try {
          firestoreProfile = await getProfileFirestore();
        } catch { /* ignore */ }
      }

      // 2. Fallback localStorage
      let localProfile = null;
      const saved = localStorage.getItem(PROFILE_STORAGE_KEY);
      if (saved) {
        localProfile = JSON.parse(saved);
      }

      // 3. Fusionner : Firestore > localStorage > default
      const base = getDefaultProfile();
      const merged = { ...base, ...localProfile, ...firestoreProfile };

      // 4. Enrichir avec Google Auth pour les champs manquants
      const enriched = enrichWithGoogleAuth(merged);

      setProfileState(enriched);

      // 5. Mettre à jour localStorage avec le profil fusionné
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(enriched));

      // 6. Si Firestore n'avait rien et qu'on a des données locales, push vers Firestore
      if (!firestoreProfile && localProfile && isAuthenticated()) {
        saveProfileFirestore(enriched).catch(() => {});
      }
    } catch (e) {
      console.warn('Erreur chargement profil:', e);
      const fallback = enrichWithGoogleAuth(getDefaultProfile());
      setProfileState(fallback);
    }
  }, [enrichWithGoogleAuth]);

  const saveProfile = useCallback((updates) => {
    const newProfile = { ...profile, ...updates };
    setProfileState(newProfile);

    // Sauvegarder en localStorage
    try {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(newProfile));
    } catch (e) {
      console.warn('Erreur sauvegarde profil localStorage:', e);
    }

    // Sauvegarder en Firestore (async, non-bloquant)
    if (isAuthenticated()) {
      saveProfileFirestore(newProfile).catch((e) => {
        console.warn('Erreur sauvegarde profil Firestore:', e);
      });
    }

    return newProfile;
  }, [profile]);

  const isProfileComplete = useMemo(() => {
    if (!profile) return false;
    return !!(profile.firstName || profile.city);
  }, [profile]);

  // ============ COMPAGNON ============
  const loadCompanion = useCallback(() => {
    try {
      const saved = localStorage.getItem(COMPANION_STORAGE_KEY);
      if (saved) {
        const companionId = JSON.parse(saved);
        setCompanion(COMPANIONS[companionId] || COMPANIONS.luna);
      } else {
        setCompanion(COMPANIONS.luna);
      }
    } catch (e) {
      setCompanion(COMPANIONS.luna);
    }
  }, []);

  const selectCompanion = useCallback((companionId) => {
    const newCompanion = COMPANIONS[companionId] || COMPANIONS.luna;
    setCompanion(newCompanion);
    localStorage.setItem(COMPANION_STORAGE_KEY, JSON.stringify(companionId));
    // Message de bienvenue
    const greetings = newCompanion.greetings;
    setCompanionMessage(greetings[Math.floor(Math.random() * greetings.length)]);
    return newCompanion;
  }, []);

  const getCompanionMessage = useCallback((type = 'encouragement', energy = 3) => {
    // Sélectionner le bon compagnon selon l'énergie
    let activeCompanion = companion || COMPANIONS.luna;
    if (energy <= 2 && activeCompanion.energyStyle !== 'low') {
      activeCompanion = COMPANIONS.zen;
    } else if (energy >= 4 && activeCompanion.energyStyle !== 'high') {
      activeCompanion = COMPANIONS.spark;
    }

    const messages = type === 'completion'
      ? activeCompanion.completions
      : activeCompanion.encouragements;

    const message = messages[Math.floor(Math.random() * messages.length)];
    setCompanionMessage(message);
    return { companion: activeCompanion, message };
  }, [companion]);

  // ============ QUÊTES ============
  const loadQuests = useCallback(() => {
    try {
      const saved = localStorage.getItem(QUESTS_STORAGE_KEY);
      if (saved) {
        const quests = JSON.parse(saved);
        // Vérifier si les quêtes sont encore valides (pas expirées)
        const today = new Date().toISOString().split('T')[0];
        const validQuests = quests.filter(q => q.date === today);
        setActiveQuests(validQuests);
      }
    } catch (e) {
      console.warn('Erreur chargement quêtes:', e);
    }
  }, []);

  const generateDailyQuests = useCallback(async (energyLevel = 3, completedTasks = []) => {
    const today = new Date().toISOString().split('T')[0];

    // Quêtes adaptées au niveau d'énergie
    const quests = [];

    if (energyLevel <= 2) {
      // Mode doux pour basse énergie
      quests.push({
        id: `quest-${today}-recovery`,
        type: 'recovery',
        title: '🌸 Journée Zen',
        description: 'Prends soin de toi, sans pression',
        objectives: [
          { type: 'complete_any', count: 2, label: '2 tâches (n\'importe lesquelles)', done: false }
        ],
        xp: 30,
        date: today,
        completed: false
      });
    } else {
      // Quêtes normales
      quests.push({
        id: `quest-${today}-daily`,
        type: 'daily',
        title: '☀️ Mission du Jour',
        description: 'Complète tes objectifs quotidiens',
        objectives: [
          { type: 'complete_vital', count: 1, label: 'Une tâche vitale', done: false },
          { type: 'complete_any', count: 3, label: '3 tâches au total', done: false }
        ],
        xp: 50,
        date: today,
        completed: false
      });

      if (energyLevel >= 4) {
        // Défi bonus pour haute énergie
        quests.push({
          id: `quest-${today}-boss`,
          type: 'boss',
          title: '👑 Mode Champion',
          description: 'Tu es en forme ? Prouve-le !',
          objectives: [
            { type: 'complete_streak', count: 4, label: '4 tâches d\'affilée', done: false }
          ],
          xp: 100,
          date: today,
          completed: false
        });
      }
    }

    // Quête secrète aléatoire (20% de chance)
    if (Math.random() < 0.2) {
      quests.push({
        id: `quest-${today}-secret`,
        type: 'secret',
        title: '🎁 Surprise !',
        description: 'Une quête mystère t\'attend...',
        objectives: [
          { type: 'complete_category', category: 'loisirs', count: 1, label: 'Un moment plaisir', done: false }
        ],
        xp: 75,
        date: today,
        completed: false,
        isSecret: true
      });
    }

    setActiveQuests(quests);
    localStorage.setItem(QUESTS_STORAGE_KEY, JSON.stringify(quests));
    return quests;
  }, []);

  const updateQuestProgress = useCallback((completedTask) => {
    setActiveQuests(prev => {
      const updated = prev.map(quest => {
        if (quest.completed) return quest;

        const newObjectives = quest.objectives.map(obj => {
          if (obj.done) return obj;

          let isDone = false;
          switch (obj.type) {
            case 'complete_any':
              isDone = true; // Toute tâche compte
              break;
            case 'complete_vital':
              isDone = completedTask.category === 'vital';
              break;
            case 'complete_category':
              isDone = completedTask.category === obj.category;
              break;
            default:
              isDone = false;
          }

          return { ...obj, done: isDone || obj.done };
        });

        const allDone = newObjectives.every(obj => obj.done);
        return { ...quest, objectives: newObjectives, completed: allDone };
      });

      localStorage.setItem(QUESTS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ============ GÉNÉRATION LIMITER (max 3/semaine) ============
  const checkGenerationLimit = useCallback(() => {
    const key = 'adhd-ai-gen-count';
    const data = JSON.parse(localStorage.getItem(key) || '{"count":0,"weekStart":0}');
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay()); // Sunday of current week
    weekStart.setHours(0, 0, 0, 0);

    if (data.weekStart !== weekStart.getTime()) {
      // New week — reset counter
      const reset = { count: 0, weekStart: weekStart.getTime() };
      localStorage.setItem(key, JSON.stringify(reset));
      return true;
    }
    return data.count < 3;
  }, []);

  const incrementGenerationCount = useCallback(() => {
    const key = 'adhd-ai-gen-count';
    const data = JSON.parse(localStorage.getItem(key) || '{"count":0,"weekStart":0}');
    localStorage.setItem(key, JSON.stringify({ ...data, count: (data.count || 0) + 1 }));
  }, []);

  const getRemainingGenerations = useCallback(() => {
    const key = 'adhd-ai-gen-count';
    const data = JSON.parse(localStorage.getItem(key) || '{"count":0,"weekStart":0}');
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    weekStart.setHours(0, 0, 0, 0);
    if (data.weekStart !== weekStart.getTime()) return 3;
    return Math.max(0, 3 - (data.count || 0));
  }, []);

  // ============ APPEL GROQ ============
  const callGroq = async (prompt) => {
    const apiKey = process.env.REACT_APP_GROQ_API_KEY;
    if (!apiKey) {
      console.warn('Clé API Groq non configurée');
      throw new Error('API Key non configurée - ajoutez REACT_APP_GROQ_API_KEY dans .env');
    }

    // Try each model until one works
    let lastError = null;
    for (const model of GROQ_MODELS) {
      try {
        console.log(`🤖 Essai avec Groq (${model})...`);

        const response = await fetch(GROQ_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            model: model,
            messages: [
              {
                role: 'system',
                content: 'Tu es un assistant de planification pour personnes ADHD. Tu réponds UNIQUEMENT en JSON valide, sans texte avant ou après. Pas de markdown, pas de code blocks, juste le JSON brut.'
              },
              {
                role: 'user',
                content: prompt
              }
            ],
            temperature: 0.7,
            max_tokens: 4096
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.warn(`⚠️ Groq ${model} erreur ${response.status}:`, errorText.slice(0, 200));
          lastError = new Error(`Groq ${model}: erreur ${response.status}`);
          continue; // Try next model
        }

        const data = await response.json();
        const text = data.choices?.[0]?.message?.content || '';

        if (!text) {
          console.warn(`⚠️ Groq ${model}: réponse vide`);
          lastError = new Error(`Groq ${model}: réponse vide`);
          continue;
        }

        console.log(`✅ Réponse reçue de Groq (${model}), parsing JSON...`);

        // Parser le JSON
        const cleanText = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        const jsonMatch = cleanText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          console.warn(`⚠️ Groq ${model}: réponse non-JSON:`, text.slice(0, 200));
          lastError = new Error(`Groq ${model}: réponse non-JSON`);
          continue;
        }

        const result = JSON.parse(jsonMatch[0]);
        console.log(`✅ Génération IA réussie avec Groq (${model})`);
        return result;

      } catch (e) {
        console.warn(`⚠️ Erreur avec Groq ${model}:`, e.message);
        lastError = e;
        // Continue to next model
      }
    }

    // All models failed
    console.error('❌ Tous les modèles Groq ont échoué');
    throw lastError || new Error('Tous les modèles Groq ont échoué');
  };

  // ============ VALIDATION JSON ============
  const validateAISchedule = (json) => {
    if (!json || typeof json !== 'object') return false;
    if (!json.weekPlan || typeof json.weekPlan !== 'object') return false;
    const days = Object.values(json.weekPlan);
    if (days.length === 0) return false;
    return days.every(day => {
      if (!day || !Array.isArray(day.events)) return false;
      return day.events.every(evt =>
        typeof evt.title === 'string' &&
        typeof evt.start === 'string' &&
        typeof evt.end === 'string' &&
        typeof evt.category === 'string'
      );
    });
  };

  // ============ GÉNÉRATION SEMAINE ============
  const generateWeek = useCallback(async (options = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const {
        selectedDays = [],
        energyLevel = 3,
        requestedActivities = [],
        customRequests = '',
        weekTheme = 'balance',   // 'productivity' | 'health' | 'social' | 'balance'
        intensityLevel = 3,      // 1-5 (léger → chargé)
        includeleisure = true,
        recentMoodAvg = null,    // average mood 1-5 from last 7 days
        calendarEvents = [],     // already-scheduled events to avoid conflicts
      } = options;

      // Check generation limit
      if (!checkGenerationLimit()) {
        setError('Limite de 3 générations IA par semaine atteinte. Réessaie la semaine prochaine !');
        return [];
      }

      // Construire le prompt enrichi
      const prompt = buildEnhancedPrompt({
        profile,
        selectedDays,
        energyLevel,
        requestedActivities,
        customRequests,
        companion,
        weekTheme,
        intensityLevel,
        includeleisure,
        recentMoodAvg,
        calendarEvents,
      });

      // Appeler Groq avec validation et retry (max 3 tentatives)
      let result;
      let usedFallback = false;
      const MAX_RETRIES = 3;
      let attempt = 0;
      while (attempt < MAX_RETRIES) {
        attempt++;
        try {
          const raw = await callGroq(
            attempt === 1
              ? prompt
              : `${prompt}\n\n## CORRECTION REQUISE (tentative ${attempt})\nTon JSON précédent était invalide. Assure-toi que weekPlan contient des dates ISO comme clés et que chaque jour a un tableau events avec au moins title, start, end, category.`
          );
          if (validateAISchedule(raw)) {
            result = raw;
            incrementGenerationCount();
            console.log(`✅ Planning généré avec Groq IA (tentative ${attempt})`);
            break;
          } else {
            console.warn(`⚠️ JSON invalide (tentative ${attempt}), retry...`);
          }
        } catch (aiError) {
          console.warn(`⚠️ Erreur IA tentative ${attempt}:`, aiError.message);
          if (attempt === MAX_RETRIES) {
            usedFallback = true;
            result = generateLocalPlan({ profile, selectedDays, energyLevel, requestedActivities });
            console.log('📋 Planning généré avec le planificateur local (fallback)');
          }
        }
      }

      if (!result) {
        usedFallback = true;
        result = generateLocalPlan({ profile, selectedDays, energyLevel, requestedActivities });
        console.log('📋 Planning généré avec le planificateur local (fallback final)');
      }

      // Convertir en événements d'agenda
      const events = convertToAgendaEvents(result, selectedDays);

      // Générer les quêtes du jour
      await generateDailyQuests(energyLevel, []);

      // Message du compagnon
      getCompanionMessage('encouragement', energyLevel);

      setLastGeneration({
        timestamp: new Date().toISOString(),
        options,
        eventsCount: events.length,
        usedAI: !usedFallback,
        source: usedFallback ? 'local' : 'groq'
      });

      return events;
    } catch (e) {
      console.error('Erreur génération:', e);
      setError(e.message || 'Erreur de génération');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [profile, companion, generateDailyQuests, getCompanionMessage]);

  // ============ SUGGESTIONS D'ACTIVITÉS ============
  const getSuggestions = useCallback(async (options = {}) => {
    setIsLoading(true);
    try {
      const { weather = 'beau', dayType = 'semaine' } = options;

      const prompt = `Tu es un assistant ADHD-friendly. Suggère 5 activités adaptées.

## PROFIL
- Ville: ${profile?.city || 'Non spécifiée'}
- Hobbies: ${(profile?.hobbies || []).join(', ') || 'Non spécifiés'}
- Lieux connus: ${(profile?.nearbyPlaces || []).map(p => `${p.name} (${p.type})`).join(', ') || 'Aucun'}

## CONTEXTE
- Météo: ${weather}
- Type de jour: ${dayType}

RÉPONDS EN JSON:
{
  "suggestions": [
    {
      "activity": "Nom",
      "category": "sport|loisirs|social|courses",
      "location": "Lieu précis",
      "duration": 60,
      "bestTime": "HH:MM",
      "prepSteps": ["étape 1"],
      "whyGood": "Adapté car...",
      "energyRequired": 1-4
    }
  ]
}`;

      const result = await callGroq(prompt);
      setSuggestions(result?.suggestions || []);
      return result?.suggestions || [];
    } catch (e) {
      console.error('Erreur suggestions:', e);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [profile]);

  // ============ RETOUR DU HOOK ============
  return {
    // État
    profile,
    isLoading,
    error,
    isProfileComplete,
    lastGeneration,

    // Compagnon
    companion,
    companions: COMPANIONS,
    companionMessage,
    selectCompanion,
    getCompanionMessage,

    // Quêtes
    activeQuests,
    questTypes: QUEST_TYPES,
    generateDailyQuests,
    updateQuestProgress,

    // Suggestions
    suggestions,
    getSuggestions,

    // Catégories
    categories: ACTIVITY_CATEGORIES,

    // Actions
    saveProfile,
    loadProfile,
    generateWeek,

    // Génération limits
    getRemainingGenerations,
    validateAISchedule,
  };
}

// ============ FONCTIONS UTILITAIRES ============

function buildEnhancedPrompt({ profile, selectedDays, energyLevel, requestedActivities, customRequests, companion, weekTheme = 'balance', intensityLevel = 3, includeleisure = true, recentMoodAvg = null, calendarEvents = [] }) {
  const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const workDayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const daysInfo = selectedDays.map(d => {
    const date = new Date(d);
    const dayOfWeek = date.getDay();
    const isWorkDay = (profile?.workSchedule?.workDays || [1,2,3,4,5]).includes(dayOfWeek);
    return {
      date: formatDate(date),
      dayName: dayNames[dayOfWeek],
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      isWorkDay: isWorkDay
    };
  });

  const energyLabels = ['très bas', 'bas', 'normal', 'élevé'];
  const energyDesc = energyLabels[energyLevel - 1] || 'normal';

  // Construire les infos de travail
  const workInfo = profile?.workSchedule || {};
  const workTypeLabels = {
    'fixed': 'Horaires fixes',
    'flexible': 'Horaires flexibles',
    'unemployed': 'Sans emploi',
    'student': 'Étudiant(e)',
    'freelance': 'Freelance'
  };

  // Construire les infos santé
  const healthInfo = profile?.healthInfo || {};
  const medicationInfo = healthInfo.hasMedication
    ? `Oui - ${healthInfo.medicationName || 'Non précisé'} à ${(healthInfo.medicationTimes || []).join(', ') || 'horaires non précisés'}`
    : 'Non';

  // Construire les infos ADHD
  const adhdInfo = profile?.adhdInfo || {};
  const challengeLabels = {
    'concentration': 'Difficultés de concentration',
    'organisation': 'Difficultés d\'organisation',
    'procrastination': 'Tendance à procrastiner',
    'hyperactivité': 'Hyperactivité',
    'gestion_temps': 'Gestion du temps',
    'transitions': 'Transitions entre tâches'
  };

  // Construire les infos transport
  const transportInfo = profile?.transport || {};
  const transportLabels = {
    'walking': 'à pied',
    'bike': 'vélo',
    'car': 'voiture',
    'public': 'transports en commun'
  };

  // Construire les préférences
  const prefs = profile?.preferences || {};
  const focusLabels = {
    'morning': 'le matin',
    'afternoon': 'l\'après-midi',
    'evening': 'le soir'
  };

  return `Tu es ${companion?.name || 'Luna'} ${companion?.emoji || '🦊'}, un assistant de planification spécialisé pour personnes ADHD.
Tu es ${companion?.personality || 'douce et encourageante'}.

Génère un planning hebdomadaire ADAPTÉ et RÉALISTE basé sur TOUTES les informations ci-dessous.

## 👤 PROFIL UTILISATEUR
- Prénom: ${profile?.firstName || 'Utilisateur'}
- Âge: ${profile?.age || 'Non spécifié'}
- Sexe: ${profile?.sex || 'Non spécifié'}
- Ville: ${profile?.city || 'Non spécifiée'}, ${profile?.country || 'France'}

## 🎯 HOBBIES ET CENTRES D'INTÉRÊT
${(profile?.hobbies || []).length > 0 ? (profile?.hobbies || []).map(h => `- ${h}`).join('\n') : '- Non spécifiés (proposer des activités variées)'}

## 📍 LIEUX FAVORIS À PROXIMITÉ
${(profile?.nearbyPlaces || []).length > 0 ? (profile?.nearbyPlaces || []).map(p => `- ${p.name} (${p.type})`).join('\n') : '- Aucun lieu défini (utiliser des lieux génériques)'}

## 💼 SITUATION PROFESSIONNELLE
- Type: ${workTypeLabels[workInfo.type] || 'Non spécifié'}
- Poste: ${workInfo.jobTitle || 'Non spécifié'}
- Horaires: ${workInfo.startTime || '09:00'} - ${workInfo.endTime || '18:00'}
- Jours de travail: ${(workInfo.workDays || [1,2,3,4,5]).map(d => workDayNames[d]).join(', ')}
- Télétravail: ${workInfo.workFromHome ? 'Oui' : 'Non'}
- Contraintes: ${workInfo.constraints || 'Aucune'}

## 💊 SANTÉ ET MÉDICAMENTS
- Médicaments: ${medicationInfo}
- Thérapie/RDV psy: ${healthInfo.hasTherapy ? `Oui - ${healthInfo.therapyDay || 'jour non précisé'} à ${healthInfo.therapyTime || 'heure non précisée'}` : 'Non'}
- Contraintes de santé: ${healthInfo.healthConstraints || 'Aucune'}

## 🧠 PROFIL ADHD
- Diagnostic: ${adhdInfo.diagnosisType || 'Non précisé'}
- Difficultés principales: ${(adhdInfo.mainChallenges || []).map(c => challengeLabels[c] || c).join(', ') || 'Non précisées'}
- Stratégies qui fonctionnent: ${(adhdInfo.copingStrategies || []).join(', ') || 'Non précisées'}

## ⏰ RYTHME QUOTIDIEN
- Réveil habituel: ${prefs.wakeTime || '07:00'}
- Coucher habituel: ${prefs.sleepTime || '23:00'}
- Petit-déjeuner: ${prefs.mealTimes?.breakfast || '07:30'}
- Déjeuner: ${prefs.mealTimes?.lunch || '12:30'}
- Dîner: ${prefs.mealTimes?.dinner || '19:30'}
- Pic de concentration: ${focusLabels[prefs.focusPeakTime] || 'le matin'}
- Besoin de pauses: ${prefs.needsBreaks ? `Oui, toutes les ${prefs.breakFrequency || 45} minutes` : 'Non'}

## 🏃 ACTIVITÉ PHYSIQUE
- Fréquence souhaitée: ${prefs.exerciseFrequency || 'modérée'}
- Préférences: ${(prefs.exercisePreference || []).join(', ') || 'Pas de préférence'}

## 👥 VIE SOCIALE
- Niveau social souhaité: ${prefs.socialLevel || 'modéré'}

## 🚗 TRANSPORT
- Transport préféré: ${transportLabels[transportInfo.preferredTransport] || 'à pied'}
- Véhicule personnel: ${transportInfo.hasVehicle ? 'Oui' : 'Non'}
- Temps de trajet max acceptable: ${transportInfo.maxTravelTime || 30} minutes

## 🎯 OBJECTIFS ACTUELS
- Priorité du moment: ${profile?.goals?.currentPriority || 'Non définie'}
- Objectifs de la semaine: ${(profile?.goals?.weeklyGoals || []).join(', ') || 'Non définis'}

## ⚡ NIVEAU D'ÉNERGIE ACTUEL
${energyDesc.toUpperCase()} (${energyLevel}/4)
${energyLevel <= 2 ? '⚠️ IMPORTANT: Planning TRÈS léger, privilégier le repos et les activités douces' : energyLevel === 4 ? '🔥 Énergie élevée: journées productives possibles' : ''}
${recentMoodAvg !== null ? `- Humeur moyenne des 7 derniers jours: ${recentMoodAvg.toFixed(1)}/5 ${recentMoodAvg < 2.5 ? '(période difficile → planning doux)' : recentMoodAvg > 4 ? '(bonne forme → peut être ambitieux)' : ''}` : ''}

## 🎯 THÈME ET INTENSITÉ DE CETTE SEMAINE
- Thème: ${({ productivity: 'Productivité (focus travail/études)', health: 'Santé (sport, bien-être, sommeil)', social: 'Social (rencontres, activités en groupe)', balance: 'Équilibre (mix de tout)' })[weekTheme] || 'Équilibre'}
- Intensité: ${intensityLevel}/5 ${intensityLevel <= 2 ? '(légère - peu de tâches, temps libre)' : intensityLevel >= 4 ? '(chargée - journées denses, productive)' : '(modérée)'}
- Inclure des loisirs: ${includeleisure ? 'Oui' : 'Non (focus uniquement sur les obligations)'}

## JOURS À PLANIFIER
${daysInfo.map(d => `- ${d.date} (${d.dayName}${d.isWeekend ? ' - Weekend' : ''})`).join('\n')}
${calendarEvents.length > 0 ? `\n## 📅 ÉVÉNEMENTS DÉJÀ PLANIFIÉS (ne pas superposer)\n${calendarEvents.slice(0, 10).map(e => `- ${e.date || ''} ${e.start || e.time || ''}: ${e.title || e.task}`).join('\n')}` : ''}

## ACTIVITÉS DEMANDÉES
${requestedActivities.map(a => `- ${a.label || a.id}`).join('\n') || 'Aucune spécifique'}

## DEMANDES PERSONNALISÉES
${customRequests || 'Aucune'}

RÉPONDS UNIQUEMENT EN JSON VALIDE:
{
  "weekPlan": {
    "YYYY-MM-DD": {
      "theme": "Thème motivant de la journée",
      "events": [
        {
          "title": "Nom de l'activité",
          "start": "HH:MM",
          "end": "HH:MM",
          "category": "vital|sport|courses|travail|loisirs|social|menage|repos|hygiene|repas",
          "location": "lieu précis (utiliser les lieux connus)",
          "subTasks": ["sous-tâche séquentielle 1", "sous-tâche 2", "sous-tâche 3", "..."],
          "tips": "conseil ADHD encourageant avec le ton du compagnon",
          "energyLevel": 1-4,
          "importance": "high|medium|low"
        }
      ],
      "dayAdvice": "conseil personnalisé pour la journée"
    }
  },
  "weekSummary": {
    "totalActivities": nombre,
    "recommendations": ["conseil 1", "conseil 2"]
  }
}

## RÈGLES CRITIQUES POUR LES SOUS-TÂCHES (subTasks)
⚠️ CHAQUE ÉVÉNEMENT DOIT AVOIR 4-8 SOUS-TÂCHES SÉQUENTIELLES DÉTAILLÉES !

Les sous-tâches doivent être:
1. **Séquentielles**: dans l'ordre chronologique d'exécution
2. **Concrètes et actionnables**: pas de verbes vagues
3. **Personnalisées**: utiliser les lieux connus du profil
4. **ADHD-friendly**: décomposer en micro-étapes

### EXEMPLES DE SOUS-TÂCHES PAR TYPE D'ACTIVITÉ:

**Courses** (utiliser le lieu: ${(profile?.nearbyPlaces || []).find(p => p.type === 'courses')?.name || 'supermarché'}):
- "Vérifier ce qui manque dans le frigo et les placards"
- "Écrire la liste des courses sur Google Keep"
- "Prendre le cabas et les sacs réutilisables"
- "Aller à ${(profile?.nearbyPlaces || []).find(p => p.type === 'courses')?.name || 'supermarché'}"
- "Suivre la liste - rayon par rayon"
- "Passer en caisse (penser carte fidélité)"
- "Retour maison et rangement immédiat"

**Sport** (utiliser le lieu: ${(profile?.nearbyPlaces || []).find(p => p.type === 'sport')?.name || 'salle de sport'}):
- "Préparer le sac de sport (tenue, serviette, bouteille)"
- "Mettre sa tenue de sport"
- "Se rendre à ${(profile?.nearbyPlaces || []).find(p => p.type === 'sport')?.name || 'la salle'}"
- "Échauffement 5-10 minutes"
- "Séance principale"
- "Étirements et retour au calme"
- "Douche et changement"

**Ménage**:
- "Mettre de la musique motivante"
- "Rassembler le matériel nécessaire"
- "Commencer par la zone la plus visible"
- "Timer 25 min - une pièce à la fois"
- "Pause 5 min si besoin"
- "Ranger le matériel"
- "Se féliciter !"

**Travail/Focus**:
- "Préparer l'espace de travail (bureau dégagé)"
- "Couper les notifications téléphone"
- "Ouvrir uniquement les outils nécessaires"
- "Définir l'objectif de la session (1 seul)"
- "Timer Pomodoro 25 min"
- "Pause 5 min (s'éloigner de l'écran)"
- "Répéter si énergie OK"

**Repas**:
- "Vérifier les ingrédients disponibles"
- "Sortir tous les ingrédients"
- "Préparer les ustensiles"
- "Cuisiner étape par étape"
- "Dresser la table"
- "Manger en pleine conscience (pas d'écran)"
- "Ranger la cuisine tout de suite"

## RÈGLES ADHD ESSENTIELLES
1. Si énergie ${energyDesc}: ${energyLevel <= 2 ? 'MAX 4-5 activités par jour, beaucoup de repos' : 'planning équilibré'}
2. TOUJOURS inclure: réveil progressif, 3 repas, routine du soir
3. Pauses de 15-30min entre activités intenses
4. Maximum 2h d'activité consécutive sans pause
5. **SOUS-TÂCHES OBLIGATOIRES** (4-8 par événement) - c'est CRUCIAL pour ADHD !
6. Utiliser les lieux EXACTS du profil: ${(profile?.nearbyPlaces || []).map(p => `${p.name} (${p.type})`).join(', ') || 'Aucun lieu défini'}
7. Intégrer les hobbies: ${(profile?.hobbies || []).join(', ')}
8. Tips encourageants avec le ton de ${companion?.name || 'Luna'} (${companion?.personality || 'encourageant'})
9. Weekend = plus de temps libre et loisirs
10. Médicaments aux bons horaires si applicable`;
}

function generateLocalPlan({ profile, selectedDays, energyLevel, requestedActivities }) {
  const weekPlan = {};

  // Récupérer les lieux connus du profil
  const sportPlace = profile?.nearbyPlaces?.find(p => p.type === 'sport')?.name;
  const coursesPlace = profile?.nearbyPlaces?.find(p => p.type === 'courses')?.name;

  selectedDays.forEach(dateStr => {
    const date = new Date(dateStr);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    const events = [];
    const wakeTime = profile?.preferences?.wakeTime || '07:00';
    const sleepTime = profile?.preferences?.sleepTime || '23:00';

    // Réveil et routine matinale
    events.push({
      title: 'Réveil et routine matinale',
      start: wakeTime,
      end: addMinutes(wakeTime, 30),
      category: 'hygiene',
      subTasks: [
        'Éteindre l\'alarme et rester assis 30 secondes',
        'Boire un grand verre d\'eau (sur la table de nuit)',
        'Ouvrir les volets/stores pour la lumière naturelle',
        'Étirements doux (2 min)',
        'Aller aux toilettes',
        'Se laver le visage à l\'eau froide',
        'Aérer la chambre (ouvrir la fenêtre)'
      ],
      tips: 'Pas de téléphone pendant 10 min - tu mérites ce calme 🌅',
      energyLevel: 1,
      importance: 'high'
    });

    // Petit-déjeuner
    events.push({
      title: 'Petit-déjeuner',
      start: addMinutes(wakeTime, 30),
      end: addMinutes(wakeTime, 55),
      category: 'repas',
      subTasks: [
        'Préparer la table (bol, cuillère, serviette)',
        'Sortir les ingrédients du frigo',
        'Préparer le café/thé',
        'S\'asseoir et manger sans écran',
        'Ranger la vaisselle dans l\'évier/lave-vaisselle'
      ],
      tips: 'Prends le temps de savourer ce moment ☕',
      energyLevel: 1,
      importance: 'high'
    });

    // Médicaments si applicable
    if (profile?.healthInfo?.hasMedication) {
      events.push({
        title: 'Médicaments du matin',
        start: addMinutes(wakeTime, 35),
        end: addMinutes(wakeTime, 40),
        category: 'vital',
        subTasks: [
          'Prendre le pilulier/boîte de médicaments',
          'Vérifier le dosage du jour',
          'Prendre avec un verre d\'eau',
          'Ranger le pilulier à sa place'
        ],
        tips: 'Essentiel pour ta journée 💊',
        energyLevel: 1,
        importance: 'high'
      });
    }

    // Déjeuner
    events.push({
      title: 'Déjeuner',
      start: '12:30',
      end: '13:15',
      category: 'repas',
      subTasks: [
        'Se laver les mains',
        'Vérifier ce qu\'il y a à manger (frigo/placards)',
        'Préparer le repas (ou réchauffer)',
        'Dresser la table',
        'Manger assis, sans écran si possible',
        'Débarrasser et ranger',
        'Petite pause digestive (5 min)'
      ],
      tips: 'Éloigne-toi du travail pendant le repas 🍽️',
      energyLevel: 2,
      importance: 'high'
    });

    // Dîner
    events.push({
      title: 'Dîner',
      start: '19:30',
      end: '20:15',
      category: 'repas',
      subTasks: [
        'Décider du menu (simple)',
        'Sortir les ingrédients',
        'Cuisiner étape par étape',
        'Dresser la table',
        'Manger tranquillement',
        'Ranger la cuisine immédiatement'
      ],
      tips: 'Moment de détente bien mérité',
      energyLevel: 2,
      importance: 'high'
    });

    // Activités selon l'énergie
    if (energyLevel >= 3 && !isWeekend) {
      events.push({
        title: 'Bloc de travail',
        start: '09:00',
        end: '12:00',
        category: 'travail',
        subTasks: [
          'Ranger le bureau (5 min max)',
          'Fermer toutes les apps non essentielles',
          'Mettre le téléphone en mode focus',
          'Écrire l\'objectif principal de la session',
          'Lancer un timer Pomodoro (25 min)',
          'Travailler sans interruption',
          'Pause 5 min (s\'éloigner de l\'écran)',
          'Répéter 2-3 cycles'
        ],
        tips: 'Technique Pomodoro: 25 min focus, 5 min pause 🎯',
        energyLevel: 3,
        importance: 'medium'
      });

      events.push({
        title: 'Pause active',
        start: '15:00',
        end: '15:20',
        category: 'repos',
        subTasks: [
          'Se lever et s\'étirer',
          'Marcher un peu (même dans l\'appart)',
          'Boire de l\'eau',
          'Regarder par la fenêtre 2 min',
          'Respiration profonde (3x)'
        ],
        tips: 'Étirements ou petite marche - ton cerveau te remerciera',
        energyLevel: 1,
        importance: 'medium'
      });
    }

    // Sport si demandé
    if (requestedActivities.some(a => a.category === 'sport' || a.id === 'sport') && energyLevel >= 2) {
      events.push({
        title: `Sport${sportPlace ? ` - ${sportPlace}` : ''}`,
        start: isWeekend ? '10:00' : '18:00',
        end: isWeekend ? '11:00' : '19:00',
        category: 'sport',
        location: sportPlace,
        subTasks: [
          'Préparer le sac de sport (tenue, serviette, bouteille)',
          'Mettre sa tenue de sport',
          `Se rendre ${sportPlace ? `à ${sportPlace}` : 'à la salle'}`,
          'Échauffement 5-10 minutes',
          'Séance principale (suivre le programme)',
          'Étirements et retour au calme',
          'Douche et changement',
          'Ranger les affaires dans le sac'
        ],
        tips: 'Échauffement obligatoire - ton corps te remerciera ! 💪',
        energyLevel: 3,
        importance: 'medium'
      });
    }

    // Courses si demandé
    if (requestedActivities.some(a => a.category === 'courses' || a.id === 'courses')) {
      events.push({
        title: `Courses${coursesPlace ? ` - ${coursesPlace}` : ''}`,
        start: '10:00',
        end: '11:30',
        category: 'courses',
        location: coursesPlace,
        subTasks: [
          'Vérifier ce qui manque dans le frigo et les placards',
          'Écrire la liste des courses (papier ou Google Keep)',
          'Prendre le cabas et les sacs réutilisables',
          'Vérifier qu\'on a la carte bancaire/carte fidélité',
          `Aller ${coursesPlace ? `à ${coursesPlace}` : 'au supermarché'}`,
          'Suivre la liste - rayon par rayon (ne pas dévier !)',
          'Passer en caisse',
          'Retour maison',
          'Ranger les courses immédiatement (frais d\'abord !)'
        ],
        tips: 'Liste écrite = moins de stress et d\'oublis 📝',
        energyLevel: 2,
        importance: 'medium'
      });
    }

    // Ménage si demandé
    if (requestedActivities.some(a => a.category === 'menage' || a.id === 'menage')) {
      events.push({
        title: 'Session ménage',
        start: isWeekend ? '11:00' : '14:00',
        end: isWeekend ? '12:00' : '15:00',
        category: 'menage',
        subTasks: [
          'Mettre de la musique motivante 🎵',
          'Choisir UNE seule pièce à faire',
          'Rassembler le matériel (produits, chiffons, aspirateur)',
          'Timer 15 min - ramasser/ranger ce qui traîne',
          'Timer 15 min - nettoyer les surfaces',
          'Timer 15 min - sol (aspirer/laver)',
          'Ranger le matériel',
          'Admirer le résultat et se féliciter !'
        ],
        tips: 'Une pièce à la fois - pas de perfection, juste du progrès 🧹',
        energyLevel: 2,
        importance: 'medium'
      });
    }

    // Hobby si disponible
    if (profile?.hobbies?.length > 0 && (isWeekend || energyLevel >= 2)) {
      const hobby = profile.hobbies[Math.floor(Math.random() * profile.hobbies.length)];
      events.push({
        title: hobby,
        start: isWeekend ? '15:00' : '20:30',
        end: isWeekend ? '17:00' : '21:30',
        category: 'loisirs',
        subTasks: [
          'Préparer l\'espace et le matériel nécessaire',
          'Mettre le téléphone en silencieux',
          'S\'installer confortablement',
          `Profiter de ${hobby.toLowerCase()} sans culpabiliser`,
          'Ranger quand c\'est fini'
        ],
        tips: 'Ton moment à toi - profites-en à fond ! ✨',
        energyLevel: 2,
        importance: 'low'
      });
    }

    // Routine du soir
    events.push({
      title: 'Routine du soir',
      start: addMinutes(sleepTime, -45),
      end: sleepTime,
      category: 'hygiene',
      subTasks: [
        'Vérifier l\'agenda de demain',
        'Préparer les affaires/vêtements du lendemain',
        'Ranger rapidement le salon (5 min max)',
        'Se brosser les dents',
        'Skincare/soins',
        'Mettre le téléphone à charger HORS de la chambre',
        'Lumière tamisée - lecture ou musique douce',
        'Gratitude: penser à 3 choses positives de la journée'
      ],
      tips: 'Évite les écrans 30 min avant - essaie la lecture ou musique douce 🌙',
      energyLevel: 1,
      importance: 'high'
    });

    // Trier par heure
    events.sort((a, b) => a.start.localeCompare(b.start));

    weekPlan[formatDate(date)] = {
      events,
      theme: isWeekend ? 'Repos & Plaisir 🌈' : 'Productivité Zen 🧘',
      dayAdvice: energyLevel <= 2
        ? 'Écoute ton corps, pas besoin d\'en faire trop aujourd\'hui'
        : 'Tu as l\'énergie, profite-en mais garde des pauses !'
    };
  });

  return { weekPlan };
}

function convertToAgendaEvents(result, selectedDays) {
  const events = [];

  if (!result?.weekPlan) return events;

  Object.entries(result.weekPlan).forEach(([dateStr, day]) => {
    if (!day.events) return;

    day.events.forEach((event, index) => {
      const mappedCategory = CATEGORY_MAP[event.category] || 'personal';

      // Combiner prepSteps et subTasks si les deux existent (compatibilité)
      const allSubTasks = [
        ...(event.prepSteps || []),
        ...(event.subTasks || [])
      ];

      events.push({
        id: `gen-${dateStr}-${index}-${Date.now()}`,
        title: event.title,
        date: dateStr,
        start: event.start,
        end: event.end,
        category: mappedCategory,
        subTasks: allSubTasks.map((s, i) => ({
          id: `sub-${dateStr}-${index}-${i}-${Math.random().toString(36).substr(2, 9)}`,
          text: s,
          completed: false
        })),
        completed: false,
        location: event.location || null,
        tips: event.tips || null,
        energyLevel: event.energyLevel || 2,
        importance: event.importance || 'medium',
        generatedBy: 'ai-planner',
        dayTheme: day.theme,
        dayAdvice: day.dayAdvice
      });
    });
  });

  return events;
}

function formatDate(date) {
  const d = typeof date === 'string' ? new Date(date) : date;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addMinutes(time, minutes) {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const newH = Math.floor(total / 60) % 24;
  const newM = total % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

export default useAIWeekGenerator;
